import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@/stores/createStore';
import { DIGITAL_FILTER_CUTOFF } from '@/tools/digitalFilter/digitalFilter.constants';
import { BASE_TOOL_COMMON_PROPS, DEFAULT_TOOL_ITEM_PROPS } from '@/tools/tool.constants';
import {
  buildToolRehydrationState,
  getNextToolParameterValue,
  getRequiredToolParameterDefinition,
} from '@/tools/tool.utilities';
import type { BaseToolPersistedState, ParameterDefinitions, ToolItem, ToolRehydratePayload } from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

type ValueWithUnits = ValueWithUnitsItem & { valid?: boolean };
type InitialValueWithUnits = Partial<ValueWithUnits>;

export interface DigitalFilterState extends BaseToolPersistedState {
  /** The input signal to apply the filter to. */
  inputSignal: ToolItem | undefined;
  /** The type of digital filter: low-pass, high-pass, band-pass, or band-stop. */
  filterType: string | undefined;
  /** The hex color used for the output signal series. */
  color: string;
  /** Primary cutoff frequency/period for the filter. */
  cutoff: InitialValueWithUnits;
  /** Secondary cutoff frequency/period, used only for band-pass and band-stop filters. */
  cutoff2: InitialValueWithUnits;
  /** The sampling rate of the input signal. */
  samplingRate: InitialValueWithUnits;
  /** The window size used for the FIR filter convolution. */
  windowSize: InitialValueWithUnits;
  /** When true, the sampling rate is estimated automatically from the input signal. */
  isAutoSamplingRate: boolean;
  /** When true, the window size is computed automatically from the cutoff. */
  isAutoWindowSize: boolean;
}

export type DigitalFilterDehydratedState = DigitalFilterState;

export interface DigitalFilterActions {
  /** Sets the filter type (low-pass, high-pass, band-pass, or band-stop). */
  setFilterType: (filterType: string) => void;
  /** Sets the hex color for the output signal series. */
  setColor: (color: string) => void;
  /** Sets the primary cutoff frequency/period for the filter. */
  setCutoff: (cutoff: ValueWithUnits) => void;
  /** Sets the secondary cutoff frequency/period (band filters only). */
  setCutoff2: (cutoff2: ValueWithUnits) => void;
  /** Sets the sampling rate of the input signal. */
  setSamplingRate: (samplingRate: ValueWithUnits) => void;
  /** Sets the window size used for the FIR filter convolution. */
  setWindowSize: (windowSize: ValueWithUnits) => void;
  /** Sets whether the sampling rate is automatically computed from the input signal. */
  setIsAutoSamplingRate: (isAutoSamplingRate: boolean) => void;
  /** Sets whether the window size is automatically computed from the cutoff. */
  setIsAutoWindowSize: (isAutoWindowSize: boolean) => void;
  /** Sets the user-visible name of the output signal. */
  setSearchName: (name: string) => void;
  /** Collapses or expands the Advanced Parameters section. */
  setAdvancedParametersCollapsedState: (collapsed: boolean) => void;
  /** Sets a named parameter item (e.g., inputSignal). */
  setParameterItem: (name: string, item: ToolItem | null | undefined) => void;
  /** Rehydrates the store from a persisted tool-for-edit payload. */
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  /** Resets the store to its initial state. */
  reset: () => void;
}

const parameterDefinitions: ParameterDefinitions = {
  inputSignal: { predicate: ['name', 'inputSignal'] },
};

const initialState: DigitalFilterState = {
  ...BASE_TOOL_COMMON_PROPS,
  filterType: undefined,
  cutoff: DIGITAL_FILTER_CUTOFF,
  cutoff2: DIGITAL_FILTER_CUTOFF,
  samplingRate: DIGITAL_FILTER_CUTOFF,
  windowSize: DIGITAL_FILTER_CUTOFF,
  isAutoSamplingRate: true,
  isAutoWindowSize: true,
  color: '',
  inputSignal: undefined,
};

export const dehydrateDigitalFilterState = (state: DigitalFilterState): DigitalFilterDehydratedState => state;

const digitalFilterActionsFactory: ActionsFactory<DigitalFilterState, DigitalFilterActions> = (
  set,
  _get,
  resetState,
) => ({
  setFilterType: (filterType) =>
    set((draft) => {
      draft.filterType = filterType;
    }),

  setColor: (color) =>
    set((draft) => {
      draft.color = color;
    }),

  setCutoff: (cutoff) =>
    set((draft) => {
      draft.cutoff = cutoff;
    }),

  setCutoff2: (cutoff2) =>
    set((draft) => {
      draft.cutoff2 = cutoff2;
    }),

  setSamplingRate: (samplingRate) =>
    set((draft) => {
      draft.samplingRate = samplingRate;
    }),

  setWindowSize: (windowSize) =>
    set((draft) => {
      draft.windowSize = windowSize;
    }),

  setIsAutoSamplingRate: (isAutoSamplingRate) =>
    set((draft) => {
      draft.isAutoSamplingRate = isAutoSamplingRate;
    }),

  setIsAutoWindowSize: (isAutoWindowSize) =>
    set((draft) => {
      draft.isAutoWindowSize = isAutoWindowSize;
    }),

  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsedState: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  setParameterItem: (name, item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(parameterDefinitions, name);
      const draftRecord = draft as unknown as Record<string, unknown>;
      const currentValue = draftRecord[name] as ToolItem | ToolItem[] | null | undefined;
      const nextValue = getNextToolParameterValue({
        definition,
        currentValue,
        item,
        toolItemProps: DEFAULT_TOOL_ITEM_PROPS,
      });
      if (nextValue.changed) {
        draftRecord[name] = nextValue.value;
      }
    }),

  rehydrateForEdit: (payload) => {
    resetState();
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: draft.advancedParametersCollapsed,
      });
      Object.assign(draft, rehydrationState.config, rehydrationState.selectedParameters, {
        originalParameters: rehydrationState.originalParameters,
      });
    });
  },

  reset: resetState,
});

const [DigitalFilterStoreProvider, useDigitalFilterStoreInstance, useDigitalFilterSelector] = createStore(
  'DigitalFilter',
  initialState,
  digitalFilterActionsFactory,
);

export { DigitalFilterStoreProvider };

/** The input signal selected for filtering. */
export const useDigitalFilterInputSignal = () => useDigitalFilterSelector((state) => state.inputSignal);
/** The active filter type (low-pass, high-pass, band-pass, band-stop). */
export const useDigitalFilterType = () => useDigitalFilterSelector((state) => state.filterType);
/** The hex color for the output signal series. */
export const useDigitalFilterColor = () => useDigitalFilterSelector((state) => state.color);
/** The primary cutoff frequency/period. */
export const useDigitalFilterCutoff = () => useDigitalFilterSelector((state) => state.cutoff);
/** The secondary cutoff frequency/period (band filters only). */
export const useDigitalFilterCutoff2 = () => useDigitalFilterSelector((state) => state.cutoff2);
/** The sampling rate of the input signal. */
export const useDigitalFilterSamplingRate = () => useDigitalFilterSelector((state) => state.samplingRate);
/** The window size for the FIR filter. */
export const useDigitalFilterWindowSize = () => useDigitalFilterSelector((state) => state.windowSize);
/** Whether sampling rate is automatically computed. */
export const useDigitalFilterIsAutoSamplingRate = () => useDigitalFilterSelector((state) => state.isAutoSamplingRate);
/** Whether window size is automatically computed. */
export const useDigitalFilterIsAutoWindowSize = () => useDigitalFilterSelector((state) => state.isAutoWindowSize);
/** The unique ID of the Digital Filter output item. */
export const useDigitalFilterId = () => useDigitalFilterSelector((state) => state.id);
/** The user-visible name of the Digital Filter output signal. */
export const useDigitalFilterName = () => useDigitalFilterSelector((state) => state.name);
/** The original parameter items hydrated from a saved tool configuration. */
export const useDigitalFilterOriginalParameters = () => useDigitalFilterSelector((state) => state.originalParameters);
/** Whether the Advanced Parameters section is collapsed in the UI. */
export const useDigitalFilterAdvancedParametersCollapsed = () =>
  useDigitalFilterSelector((state) => state.advancedParametersCollapsed);

/** Returns the Digital Filter store actions for updating state. */
export const useDigitalFilterActions = () => useDigitalFilterStoreInstance().actions;

/**
 * @deprecated For legacy singleton bridge only. Non-React consumers (action files) use the singleton
 * created from this in core.stores.ts. DO NOT use in new code.
 */
export function legacyCreateDigitalFilterStore() {
  return createStoreInstance<DigitalFilterState, DigitalFilterActions>(initialState, digitalFilterActionsFactory);
}

export const usePersistDigitalFilterStore = () => {
  const register = useRegisterPersistedStore();
  const store = useDigitalFilterStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqDigitalFilterStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateDigitalFilterState,
    });
  }, [register, store]);
};
